import React, { forwardRef, MutableRefObject, ReactElement, RefAttributes } from 'react';
import ReactSelect, { GroupBase, Props } from 'react-select';
import ReactSelectType from 'react-select/dist/declarations/src/Select';
import { useTestId } from '../../hooks/useTestId/useTestId';
import styled, { useTheme } from 'styled-components';
import { useSelectCss } from './selectStyleOverrides';
import { ClearIndicator, Control, DropdownIndicator, MultiValueContainer, MultiValueLabel, MultiValueRemove } from './SelectComponents';
import merge from 'lodash.merge';
import { DEFAULT_INPUT_BOTTOM_PAD, IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { IForceFocusOutline, IInvalidProps } from '../../fundamentals/actions';
import { DEFAULT_INPUT_SIZE, DEFAULT_INPUT_WIDTH, InputSize, ITextFieldCssProps, ITextFieldProps } from '../TextField/TextField';
import { BaseDiv } from '../../common/BaseElements';
import { ITheme } from '../../themes/theme';

export interface ISelectProps extends IInvalidProps, IGutterAsMarginProps {
  size?: InputSize;
}

export interface ISelectCssProps
  extends Omit<ISelectProps, 'size'>,
    Pick<Props, 'isClearable'>,
    IForceFocusOutline,
    Pick<ITextFieldCssProps, 'inputSize' | 'styleWidth'>,
    Pick<ITextFieldProps, 'disabled'> {}

const SelectContainer = styled(BaseDiv)<ISelectCssProps>`
  ${useSelectCss};
  ${useGutterAsMarginCss()};
`;

export const REACT_SELECT_PREFIX = `rselect`;

export type SelectType = <Option = unknown, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: ISelectProps & Props<Option, IsMulti, Group> & RefAttributes<ReactSelectType<Option, IsMulti, Group>>
) => ReactElement;

export type SelectRefType<Option, IsMulti extends boolean, Group extends GroupBase<Option>> =
  | ((instance: ReactSelectType<Option, IsMulti, Group> | null) => void)
  | MutableRefObject<ReactSelectType<Option, IsMulti, Group> | null>
  | null;

export const Select = forwardRef(
  <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: ISelectProps & Props<Option, IsMulti, Group>,
    ref: SelectRefType<Option, IsMulti, Group>
  ) => {
    const {
      invalid,
      size,
      components,
      gutter,
      gutterBottom = DEFAULT_INPUT_BOTTOM_PAD,
      gutterLeft,
      gutterRight,
      gutterTop,
      isClearable,
      isDisabled,
      width = DEFAULT_INPUT_WIDTH,
      ...rest
    } = props;
    const { generateTestId } = useTestId(rest, 'Select');
    const customComponents = merge({ Control, DropdownIndicator, ClearIndicator, MultiValueContainer, MultiValueLabel, MultiValueRemove }, components);
    const theme = useTheme() as ITheme;
    return (
      <SelectContainer
        invalid={invalid}
        inputSize={size ?? DEFAULT_INPUT_SIZE}
        gutter={gutter}
        gutterBottom={gutterBottom}
        gutterLeft={gutterLeft}
        gutterRight={gutterRight}
        gutterTop={gutterTop}
        forceOutline={true}
        isClearable={isClearable}
        disabled={isDisabled}
        styleWidth={width}
        {...generateTestId()}
      >
        <ReactSelect
          classNamePrefix={REACT_SELECT_PREFIX}
          ref={ref}
          selectSize={size}
          components={customComponents}
          isClearable={isClearable}
          isDisabled={isDisabled}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              background: theme.colours.eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              backgroundColor: theme.colours.eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND,
              zIndex: 1000
            })
          }}
          {...rest}
        />
      </SelectContainer>
    );
  }
) as SelectType;

declare module 'react-select/dist/declarations/src/Select' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    selectSize?: ITextFieldProps['inputSize'];
    width?: ITextFieldProps['width'];
  }
}
